/**
 * Documents for one property, apartment or room — one panel, three levels.
 *
 * The apartment tab used to own this markup. Property- and room-level
 * documents are the same list with a different target, so they share the
 * component rather than growing two near-copies that drift on the first fix
 * applied to only one of them.
 *
 * Each row carries an optional message from whoever uploaded it. It is edited
 * in place rather than captured at upload time: notes are most often written
 * after the fact ("superseded by the 2026 version"), and forcing one into the
 * upload step would mean either a blocking prompt or a note nobody fills in.
 */
import { useRef, useState } from "react";
import { FileText, MessageSquarePlus, Trash2, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { formatDateTime } from "@/lib/v2-format";
import { StoredFileLink } from "@/components/shared/StoredFileLink";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  useApartmentDocuments,
  useDeleteApartmentDocument,
  usePropertyLevelDocuments,
  useRoomDocuments,
  useUpdateDocumentMessage,
  useUploadApartmentDocument,
  type ApartmentDocument,
  type DocumentScope,
} from "@/hooks/v2/useApartmentDocuments";

/**
 * The three levels this panel is mounted at. DocumentScope also has 'tenant',
 * which deliberately does NOT belong here: a tenancy's documents are shown on
 * the tenant profile (TenantDocumentsSection), which resolves the uploader's
 * name so staff can see WHICH owner filed a document — something this panel
 * has no reason to carry.
 */
type PanelScope = Exclude<DocumentScope, "tenant">;

type Props = {
  scope: PanelScope;
  propertyId: number;
  apartmentId?: number;
  roomId?: number;
  /** Who the upload is attributed to. Owners may only ever delete their own. */
  source?: "admin" | "owner";
  title?: string;
  description?: string;
};

const SCOPE_NOUN: Record<PanelScope, string> = {
  property: "property",
  apartment: "apartment",
  room: "room",
};

/**
 * Each panel names its own level, so a tab labelled only "Documents" can never
 * leave you wondering which set you are looking at.
 *
 * Deliberately NOT "Owner documents" across the board: the same list holds
 * staff uploads and owner uploads, and calling all of it the owner's would
 * mislabel half the rows. Provenance is already carried per row by the "From
 * owner" badge, which is where that distinction belongs.
 */
const SCOPE_TITLE: Record<PanelScope, string> = {
  property: "Property documents",
  apartment: "Apartment documents",
  room: "Room documents",
};

export function DocumentsPanel({
  scope,
  propertyId,
  apartmentId,
  roomId,
  source = "admin",
  title,
  description,
}: Props) {
  const { t } = useLanguage();
  // Only the query matching this scope is enabled; the other two sit idle on a
  // null id rather than firing and being thrown away.
  const propertyDocs = usePropertyLevelDocuments(
    scope === "property" ? propertyId : null
  );
  const apartmentDocs = useApartmentDocuments(
    scope === "apartment" ? (apartmentId ?? null) : null
  );
  const roomDocs = useRoomDocuments(scope === "room" ? (roomId ?? null) : null);
  const active =
    scope === "property"
      ? propertyDocs
      : scope === "apartment"
        ? apartmentDocs
        : roomDocs;
  const docs = active.data;
  const isLoading = active.isLoading;

  const uploadMut = useUploadApartmentDocument();
  const deleteMut = useDeleteApartmentDocument();
  const messageMut = useUpdateDocumentMessage();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftMessage, setDraftMessage] = useState("");

  function handleFilePicked(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    const handlers = {
      onSuccess: () =>
        toast({ title: "Document uploaded", description: file.name }),
      onError: (err: unknown) =>
        toast({
          title: "Upload failed",
          description: err instanceof Error ? err.message : "Unknown error",
          variant: "destructive",
        }),
    };

    // Three explicit calls rather than one built object: the upload params are
    // a discriminated union, and a ternary hands the checker the whole union
    // at once instead of one narrowed branch at a time. Written out, an
    // omitted target is a compile error — which is the point of the union.
    if (scope === "property") {
      uploadMut.mutate({ scope, propertyId, file, source }, handlers);
    } else if (scope === "apartment") {
      if (apartmentId == null) return;
      uploadMut.mutate(
        { scope, propertyId, apartmentId, file, source },
        handlers
      );
    } else {
      if (roomId == null) return;
      uploadMut.mutate({ scope, propertyId, roomId, file, source }, handlers);
    }
  }

  function handleDelete(doc: ApartmentDocument) {
    setPendingDeleteId(doc.id);
    deleteMut.mutate(doc, {
      onSettled: () => setPendingDeleteId(null),
      onError: (err) =>
        toast({
          title: "Could not delete",
          description: err instanceof Error ? err.message : "Unknown error",
          variant: "destructive",
        }),
    });
  }

  function saveMessage(doc: ApartmentDocument) {
    messageMut.mutate(
      { doc, message: draftMessage },
      {
        onSuccess: () => setEditingId(null),
        onError: (err) =>
          toast({
            title: "Could not save the note",
            description: err instanceof Error ? err.message : "Unknown error",
            variant: "destructive",
          }),
      }
    );
  }

  const noun = SCOPE_NOUN[scope];
  const heading = title ?? SCOPE_TITLE[scope];

  return (
    <Card className="rounded-2xl border-border/60 shadow-sm">
      <CardHeader className="flex flex-row items-start justify-between gap-3 border-b bg-muted/15">
        <div>
          <CardTitle className="text-lg">{heading}</CardTitle>
          <CardDescription>
            {description ??
              `Files attached to this ${noun}. Owner-uploaded documents are labeled and read-only here — only the owner can remove those.`}
          </CardDescription>
        </div>
        <Button
          type="button"
          size="sm"
          className="gap-1.5 shrink-0"
          disabled={uploadMut.isPending}
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="h-4 w-4" />
          {uploadMut.isPending ? "Uploading…" : "Upload"}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={handleFilePicked}
        />
      </CardHeader>
      <CardContent className="pt-6">
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading documents…</p>
        ) : !docs || docs.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            No documents yet.
          </p>
        ) : (
          <div className="divide-y">
            {docs.map((d) => {
              // Staff manage anything; an owner-sourced row is read-only on the
              // admin side, matching the RLS that governs it.
              const ownerRow = d.source === "owner";
              const canManage = source === "admin" ? !ownerRow : ownerRow;
              return (
                <div key={d.id} className="py-3 space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    {/* A bare contracts path, signed on click (COL-436). */}
                    <StoredFileLink
                      value={d.file_url}
                      bucket="contracts"
                      errorText={t(
                        "admin.docs.openFailed",
                        "Could not open the document."
                      )}
                      className="flex items-center gap-3 min-w-0 group"
                    >
                      <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate group-hover:underline">
                          {d.title}
                        </div>
                        {/* Date AND time. Staff reconcile an owner's upload
                            against a phone call or an email thread from the
                            same afternoon, and a bare date cannot settle which
                            of two files arrived first. */}
                        <div className="text-xs text-muted-foreground">
                          {formatDateTime(d.created_at) ?? "—"}
                        </div>
                      </div>
                    </StoredFileLink>
                    <div className="flex items-center gap-2 shrink-0">
                      {/* WHO uploaded it, not which side they were on. "From
                          owner" was the same badge on every owner row, so a
                          property held by three co-owners attributed all of
                          their files to nobody. The sky tone still carries the
                          "not ours" signal a staff reader scans for. */}
                      {ownerRow && (
                        <Badge
                          variant="outline"
                          className="rounded-full bg-sky-50 text-sky-700 border-sky-200"
                          title={
                            d.uploader_name
                              ? `Uploaded by ${d.uploader_name} (owner)`
                              : "Uploaded by the owner"
                          }
                        >
                          {d.uploader_name ?? "From owner"}
                        </Badge>
                      )}
                      {canManage && (
                        <>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => {
                              setEditingId(d.id);
                              setDraftMessage(d.message ?? "");
                            }}
                            aria-label={`${d.message ? "Edit" : "Add"} note on ${d.title}`}
                          >
                            <MessageSquarePlus className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            disabled={pendingDeleteId === d.id}
                            onClick={() => handleDelete(d)}
                            aria-label={`Delete ${d.title}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  {editingId === d.id ? (
                    <div className="space-y-2 pl-7">
                      <Textarea
                        value={draftMessage}
                        onChange={(e) => setDraftMessage(e.target.value)}
                        rows={2}
                        placeholder="What is this file, or what should the other side know about it?"
                      />
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          size="sm"
                          disabled={messageMut.isPending}
                          onClick={() => saveMessage(d)}
                        >
                          Save note
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditingId(null)}
                        >
                          <X className="h-3.5 w-3.5 mr-1" />
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : d.message ? (
                    <p className="pl-7 text-sm text-muted-foreground whitespace-pre-wrap">
                      {d.message}
                    </p>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
